/*
 * This implements the protocol specified by the Authentication Handshake document in SharePoint:
 * https://salientmotion.sharepoint.com/:w:/s/Engineering/Ee7gGP01oAZGkCMhfoEoAP4BfJDqb4RB6HXj6mkk2cqxgA?e=0Ui7nS
 */

import { toByteArray } from 'base64-js'
import { ed25519, x25519 } from '@noble/curves/ed25519'
import * as utils from '@noble/curves/utils'
import { default as aesjs } from 'aes-js'

import { KeyPairDoc } from 'utils/firestore'
import {
  StrictNumericEnumParam,
  areByteArraysEqual,
  assertUnreachable,
  bytesAllZero
} from 'utils/common'
import { CA_PUBKEYS_FOR_MCU_CERTS, CaPubKey } from './keys'
import { hidRequest, setHidRequestLogging } from '../hid'
import {
  AuthStatus,
  CertExchangeResult,
  EncChallengeResult,
  PacketId,
  serializeCertExchange1Request,
  serializeCertExchange2Request,
  serializeEncChallenge1Request,
  serializeEncChallenge2Request,
  serializeKeepAliveRequest
} from 'utils/common-types'

const DEBUG_TIMING = true
const DEBUG_PACKET_LOGGING = false // WARNING: sensitive data if enabled!
const X25519_KEY_SIZE = 32
const ED25519_SIGNATURE_SIZE = 64
const CHALLENGE_SIZE = 16
const CERT_SIZE = X25519_KEY_SIZE + 4 + ED25519_SIGNATURE_SIZE // 100 bytes
const CERT_HALF_SIZE = CERT_SIZE >> 1
const NUM_RNG_FAIL_RETRIES = 3

const DELAY_STATE_MS = 1500 // 1.5s; how long to let device warm up before starting handshake?
const KEEPALIVE_TIMEOUT_MS = 250 // 250ms
const KEEPALIVE_INTERVAL_MS = 3000 // 3s (MCU times us out after 5s)
const CERT_EXCHANGE_1_TIMEOUT = 30 // 30ms
const CERT_EXCHANGE_2_TIMEOUT = 2000 // 2s (stm32g474 actual time 1400ms)
const ENC_CHALLENGE_1_TIMEOUT = 1000 // 1s (stm32g474 actual time 575ms)
const ENC_CHALLENGE_2_TIMEOUT = 1000 // 2s (stm32g474 actual time 575ms)

export type AccountKeyPair = {
  // The ID of the CA public key used by MCUs to verify this S-Lab account public key
  caId: number
  // 64-byte signature by above CA public key of the S-Lab account public key
  caSig: Uint8Array
  // 32-byte X25519 private key unique to this S-Lab account. SENSITIVE DATA.
  privkey: Uint8Array
  // 32-byte X25519 public key unique to this S-Lab account
  pubkey: Uint8Array
}

export const getAndVerifyAccountKeyPair = (keyPairDoc: KeyPairDoc): AccountKeyPair => {
  const { caId, caSig: caSigStr, privkey: privkeyStr, pubkey: pubkeyStr } = keyPairDoc

  try {
    const caSig = toByteArray(caSigStr)
    const privkey = toByteArray(privkeyStr)
    const pubkey = toByteArray(pubkeyStr)

    if (!isFinite(caId) || !Number.isInteger(caId) || caId < 0 || caId > 0xffffffff) {
      throw new AccountKeyLoadError(
        `CA ID for this S-Lab account's key is ${caId} which is not a 32-bit unsigned integer`
      )
    }

    if (caSig.length !== ED25519_SIGNATURE_SIZE)
      throw new AccountKeyLoadError(`unexpected caSig length: ${caSig.length}`)
    if (privkey.length !== X25519_KEY_SIZE)
      throw new AccountKeyLoadError(`unexpected privkey length: ${privkey.length}`)
    if (pubkey.length !== X25519_KEY_SIZE)
      throw new AccountKeyLoadError(`unexpected pubkey length: ${pubkey.length}`)

    const pubkeyFromPrivkey = x25519.getPublicKey(privkey)
    if (!areByteArraysEqual(pubkey, pubkeyFromPrivkey)) {
      throw new AccountKeyLoadError('public key does not correspond to private key')
    }

    return {
      caId,
      caSig,
      privkey,
      pubkey
    }
  } catch (e) {
    if (e instanceof AccountKeyLoadError) throw e
    throw new AccountKeyLoadError(`unexpected error getting or verifying keypair: ${e}`)
  }
}

export enum HandshakeStatus {
  NotSupported,
  Delaying,
  InProgress,
  Successful,
  Failed
}

export type ErrorFromDevice =
  | {
      packetId: PacketId.CERT_EXCHANGE_2
      result: CertExchangeResult
    }
  | {
      packetId: PacketId.ENC_CHALLENGE_2
      result: EncChallengeResult
    }

export const mcuErrToStr = (mcuErr: ErrorFromDevice): string => {
  let errStr = '<unknown error from MCU>'
  if (mcuErr.packetId === PacketId.CERT_EXCHANGE_2) {
    errStr = 'MCU error from Cert Exchange 2: '
    switch (mcuErr.result) {
      case CertExchangeResult.SUCCESS:
        errStr += 'SUCCESS???'
        break
      case CertExchangeResult.BAD_CHALLENGE_STEP:
        errStr += 'BAD_CHALLENGE_STEP'
        break
      case CertExchangeResult.KEY_NOT_FOUND:
        errStr += 'KEY_NOT_FOUND'
        break
      case CertExchangeResult.BAD_SIGNATURE:
        errStr += 'BAD_SIGNATURE'
        break
      default:
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        errStr += `<unknown result code ${(mcuErr as any).result}>`
    }
  } else if (mcuErr.packetId === PacketId.ENC_CHALLENGE_2) {
    errStr = 'MCU error from Enc Challenge 2: '
    switch (mcuErr.result) {
      case EncChallengeResult.SUCCESS:
        errStr += 'SUCCESS???'
        break
      case EncChallengeResult.BAD_CHALLENGE_STEP:
        errStr += 'BAD_CHALLENGE_STEP'
        break
      case EncChallengeResult.BAD_DECRYPTION:
        errStr += 'BAD_DECRYPTION'
        break
      default:
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        errStr += `<unknown result code ${(mcuErr as any).result}>`
    }
  }
  return errStr
}

export type ErrorFromSLab = {
  packetId:
    | PacketId.CERT_EXCHANGE_1
    | PacketId.CERT_EXCHANGE_2
    | PacketId.ENC_CHALLENGE_1
    | PacketId.ENC_CHALLENGE_2
  result?: McuCertErrorType
  msg: string
}

// Note: only constructed if completely valid
export type McuCert = {
  pubkey: Uint8Array // 32 bytes
  caId: number // 4 bytes, unsigned
  caSig: Uint8Array // 64 bytes; we don't need it anymore though
}

export type StatusUpdateCb = (status?: HandshakeStatus) => void

// Round to tenths, for debug timing
const rt = (n: number): number => Math.round(n * 10) / 10

export class Handshaker {
  status: HandshakeStatus
  statusUpdateCb: StatusUpdateCb | undefined
  // At most one of the following two will be set, and only on failure.
  mcuErr?: ErrorFromDevice
  err?: ErrorFromSLab
  private readonly keypair: AccountKeyPair
  private challengeA: Uint8Array | undefined
  private keepAliveTimer?: KeepAliveTimer

  // Note: only supply challengeA for testing, to make test deterministic
  constructor(
    keypair: AccountKeyPair,
    keepAliveTimer?: KeepAliveTimer,
    statusUpdateCb?: StatusUpdateCb,
    challengeA?: Uint8Array
  ) {
    if (challengeA !== undefined && challengeA.length !== CHALLENGE_SIZE) {
      throw new AuthHandshakeError(`unexpected challengeA length: ${challengeA.length}`)
    }

    this.status = HandshakeStatus.Delaying
    this.statusUpdateCb = statusUpdateCb
    this.keypair = keypair
    this.challengeA = challengeA
    this.keepAliveTimer = keepAliveTimer
  }

  // Call exactly once on device connection
  async run(device: HIDDevice) {
    // This function must be called before returning, when the handshake is successful
    const success = () => {
      this.status = HandshakeStatus.Successful
      this.statusUpdateCb?.(this.status)
      this.keepAliveTimer?.start()
    }

    this.keepAliveTimer?.stop()
    try {
      const { authStatus } = await hidRequest(
        device,
        PacketId.KEEP_ALIVE,
        serializeKeepAliveRequest({}),
        KEEPALIVE_TIMEOUT_MS
      )
      if (!isReHandshakeNeeded(authStatus)) {
        // Skip handshake
        return success()
      }
    } catch (e) {
      console.error(e)
      this.status = HandshakeStatus.NotSupported
      this.statusUpdateCb?.(this.status)
      return
    }

    // Wait for device to warm up
    await new Promise((resolve) => setTimeout(resolve, DELAY_STATE_MS))
    this.status = HandshakeStatus.InProgress
    this.statusUpdateCb?.(this.status)

    let tTotal
    if (DEBUG_TIMING) tTotal = performance.now()
    if (DEBUG_PACKET_LOGGING) setHidRequestLogging(true)
    let _e: unknown
    try {
      let t
      const ourCert = this.makeCertBytes()
      const sLabCertFirstHalf = ourCert.subarray(0, CERT_HALF_SIZE)
      const sLabCertLastHalf = ourCert.subarray(CERT_HALF_SIZE)

      // 1) Cert Exchange 1
      if (DEBUG_TIMING) t = performance.now()
      const { mcuCertFirstHalf } = await hidRequest(
        device,
        PacketId.CERT_EXCHANGE_1,
        serializeCertExchange1Request({ sLabCertFirstHalf }),
        CERT_EXCHANGE_1_TIMEOUT
      )
      if (DEBUG_TIMING && t !== undefined)
        console.log('DEBUG_TIMING(CERT_EXCHANGE_1)', rt(performance.now() - t))
      if (mcuCertFirstHalf.length !== CERT_HALF_SIZE)
        throw new AuthHandshakeError('unexpected! mcu cert half len')

      // 2) Cert Exchange 2
      if (DEBUG_TIMING) t = performance.now()
      const { result: result1, mcuCertLastHalf } = await hidRequest(
        device,
        PacketId.CERT_EXCHANGE_2,
        serializeCertExchange2Request({ sLabCertLastHalf }),
        CERT_EXCHANGE_2_TIMEOUT
      )
      if (DEBUG_TIMING && t !== undefined)
        console.log('DEBUG_TIMING(CERT_EXCHANGE_2)', rt(performance.now() - t))
      if (result1 !== CertExchangeResult.SUCCESS) {
        this.mcuErr = {
          packetId: PacketId.CERT_EXCHANGE_2,
          result: result1
        }
        return
      }
      let mcuCertBytes = new Uint8Array(new ArrayBuffer(CERT_SIZE)) // Reassemble for verifying
      mcuCertBytes.set(mcuCertFirstHalf, 0)
      mcuCertBytes.set(mcuCertLastHalf, CERT_HALF_SIZE)

      // Throws if invalid
      let mcuCert
      try {
        mcuCert = this.extractAndVerifyMcuCert(mcuCertBytes, CA_PUBKEYS_FOR_MCU_CERTS)
      } catch (e) {
        if (e instanceof McuCertVerifyError) {
          this.err = {
            packetId: PacketId.CERT_EXCHANGE_2,
            result: e.errorType,
            msg: e.message
          }
          return
        } else {
          throw e
        }
      }

      // Perform ECDH on our private key with MCU's public key to get 32 byte shared secret,
      // and use it for AES decryption in ECB mode.
      const sharedSecret = x25519.getSharedSecret(this.keypair.privkey, mcuCert.pubkey)
      const aesEcb = new aesjs.ModeOfOperation.ecb(sharedSecret)

      // 3) Enc Challenge 1
      let rngFailRetries = NUM_RNG_FAIL_RETRIES
      let _challengeB
      do {
        this.err = undefined
        this.challengeA ??= utils.randomBytes(CHALLENGE_SIZE)
        if (DEBUG_TIMING) t = performance.now()
        const { encChallengeA, challengeB } = await hidRequest(
          device,
          PacketId.ENC_CHALLENGE_1,
          serializeEncChallenge1Request({ challengeA: this.challengeA }),
          ENC_CHALLENGE_1_TIMEOUT
        )
        if (DEBUG_TIMING && t !== undefined)
          console.log('DEBUG_TIMING(ENC_CHALLENGE_1)', rt(performance.now() - t))
        _challengeB = challengeB

        // Decrypt what MCU encrypted and compare to original
        if (!utils.equalBytes(this.challengeA, aesEcb.decrypt(encChallengeA))) {
          if (bytesAllZero(challengeB)) {
            this.err = {
              packetId: PacketId.ENC_CHALLENGE_1,
              msg: 'MCU RNG failure -- not ready yet?'
            }
          } else {
            this.err = {
              packetId: PacketId.ENC_CHALLENGE_1,
              msg: 'MCU did not correctly encrypt challengeA'
            }
            return
          }
        }
      } while (isDefined(this.err) && rngFailRetries-- > 0)
      if (isDefined(this.err)) return // Max retries exceeded

      // 4) Enc Challenge 2
      const encChallengeB = aesEcb.encrypt(_challengeB)
      if (DEBUG_TIMING) t = performance.now()
      const { result: result2 } = await hidRequest(
        device,
        PacketId.ENC_CHALLENGE_2,
        serializeEncChallenge2Request({ encChallengeB }),
        ENC_CHALLENGE_2_TIMEOUT
      )
      if (DEBUG_TIMING && t !== undefined)
        console.log('DEBUG_TIMING(ENC_CHALLENGE_2)', rt(performance.now() - t))
      if (result2 !== EncChallengeResult.SUCCESS) {
        this.mcuErr = {
          packetId: PacketId.ENC_CHALLENGE_2,
          result: result2
        }
        return
      }

      return success()
    } catch (e) {
      _e = e
      throw e
    } finally {
      // See promise rejection in hidRequest
      if (typeof _e === 'string') {
        const matchObj = _e.match(/\[ (\d+) \]: Device request timeout/)
        if (matchObj) {
          const packetId = parseInt(matchObj[1])
          const packetIdStr = PacketId[packetId] ?? `<unknown packet ID ${packetId}>`
          this.err = {
            packetId,
            msg: `Timeout waiting for ${packetIdStr} response`
          }
        }
      }
      if (DEBUG_PACKET_LOGGING) setHidRequestLogging(false)
      if (DEBUG_TIMING && tTotal !== undefined)
        console.log('DEBUG_TIMING(TOTAL)', rt(performance.now() - tTotal))
      if ((this.status as HandshakeStatus) !== HandshakeStatus.Successful) {
        this.status = HandshakeStatus.Failed
        this.statusUpdateCb?.(this.status)
      }
    }
  }

  // Note: no length checks on pubkey or caSig! Those should've been checked
  // prior to Handshaker creation.
  makeCertBytes(): Uint8Array {
    let buf = new ArrayBuffer(CERT_SIZE)
    let dv = new DataView(buf)
    let arr = new Uint8Array(buf)

    // 1. An X25519 public key (32 bytes)
    arr.set(this.keypair.pubkey, 0)
    // 2. The 32-bit integer ID of the key used for signing this certificate (4 bytes).
    dv.setUint32(X25519_KEY_SIZE, this.keypair.caId, true)
    // 3. The signature of the public key in #1. (64 bytes)
    arr.set(this.keypair.caSig, X25519_KEY_SIZE + 4)

    return arr
  }

  // Throws McuCertVerifyError if certificate verification fails
  extractAndVerifyMcuCert(mcuCertBytes: Uint8Array, caPubs: CaPubKey[]): McuCert {
    if (mcuCertBytes.length !== CERT_SIZE) throw new AuthHandshakeError('unexpected! mcu cert len')
    const dv = new DataView(mcuCertBytes.buffer, mcuCertBytes.byteOffset, mcuCertBytes.byteLength)
    // An X25519 public key (32 bytes)
    const pubkey = mcuCertBytes.subarray(0, X25519_KEY_SIZE)

    // The 32-bit integer ID of the key used for signing this certificate (4 bytes).
    const caId = dv.getUint32(X25519_KEY_SIZE, true)
    const caPubKey = caPubs.find((k) => k.id === caId)
    if (caPubKey === undefined) {
      throw new McuCertVerifyError(McuCertErrorType.CaPublicKeyNotFound, caId)
    }

    // The signature of the public key in #X25519 public key1. (64 bytes)
    const caSig = mcuCertBytes.subarray(X25519_KEY_SIZE + 4, CERT_SIZE)
    if (!ed25519.verify(caSig, pubkey, caPubKey.pub)) {
      throw new McuCertVerifyError(McuCertErrorType.BadSignature, caId)
    }

    return { pubkey, caId, caSig }
  }
}

export type TriggerHandshakeCb = (device: HIDDevice) => void

// When this is created, it's in a stopped state.
export class KeepAliveTimer {
  private device: HIDDevice
  private stopped: boolean
  // Kudos to Akxe on StackOverflow for the correct type here:
  // https://stackoverflow.com/questions/45802988/typescript-use-correct-version-of-settimeout-node-vs-window
  private timerId: ReturnType<typeof setTimeout> | undefined
  private triggerHandshakeCb: TriggerHandshakeCb
  constructor(device: HIDDevice, triggerHandshakeCb: TriggerHandshakeCb) {
    this.device = device
    this.stopped = true
    this.triggerHandshakeCb = triggerHandshakeCb
  }

  start() {
    this.stop()
    this.stopped = false
    this.timerId = setTimeout(this.sendKeepAlive.bind(this), KEEPALIVE_INTERVAL_MS)
  }

  stop() {
    this.stopped = true
    if (this.timerId !== undefined) {
      clearTimeout(this.timerId)
      this.timerId = undefined
    }
  }

  private async sendKeepAlive() {
    this.timerId = undefined
    let retries = 3
    let authStatus: AuthStatus | undefined
    while (retries--) {
      try {
        const result = await hidRequest(
          this.device,
          PacketId.KEEP_ALIVE,
          serializeKeepAliveRequest({}),
          KEEPALIVE_TIMEOUT_MS
        )
        authStatus = result.authStatus
      } catch (e) {
        if (typeof e !== 'string' || !e.match(/Device request timeout$/)) {
          throw e
        }
      }
    }
    if (this.stopped) return
    if (!isDefined(authStatus) || isReHandshakeNeeded(authStatus)) {
      this.stop()
      this.triggerHandshakeCb(this.device)
      return
    }
    // OK this time. Wait and resend.
    this.timerId = setTimeout(this.sendKeepAlive.bind(this), KEEPALIVE_INTERVAL_MS)
  }
}

const isReHandshakeNeeded = <Value extends AuthStatus>(
  authStatus: StrictNumericEnumParam<AuthStatus, Value>
): boolean => {
  switch (authStatus) {
    case AuthStatus.AUTHENTICATED:
      return false
    case AuthStatus.NEEDS_REAUTH:
      return true
    default:
      return assertUnreachable(authStatus)
  }
}

export class AuthHandshakeError extends Error {
  constructor(m: string) {
    super(m)

    // Set the prototype explicitly.
    Object.setPrototypeOf(this, AuthHandshakeError.prototype)
  }
}

export enum McuCertErrorType {
  CaPublicKeyNotFound,
  BadSignature
}

export class McuCertVerifyError extends AuthHandshakeError {
  errorType: McuCertErrorType

  constructor(errorType: McuCertErrorType, caId: number) {
    let m = "Error while verifying MCU's cert: "
    const caIdStr = '0x' + caId.toString(16).padStart(8, '0')
    switch (errorType) {
      case McuCertErrorType.CaPublicKeyNotFound:
        m += `unrecognized CA public key ID: ${caIdStr}`
        break
      case McuCertErrorType.BadSignature:
        m += `bad signature for CA ID: ${caIdStr}`
        break
      default:
        m += `<unknown error type ${errorType}`
    }
    super(m)

    this.errorType = errorType

    // Set the prototype explicitly.
    Object.setPrototypeOf(this, McuCertVerifyError.prototype)
  }
}

export class AccountKeyLoadError extends AuthHandshakeError {
  constructor(m: string) {
    super(m)

    // Set the prototype explicitly.
    Object.setPrototypeOf(this, AccountKeyLoadError.prototype)
  }
}
